import apiClient from './apiClient';

const handleError = (error) => {
  const message =
    error.response?.data?.error?.message ||
    error.response?.data?.message ||
    error.message;
  const formatted = new Error(message || 'Integration request failed');
  // Preserve only stable, non-sensitive metadata. In particular, do not copy
  // the server's arbitrary `details` object onto an error rendered by React.
  formatted.code = error.response?.data?.error?.code || error.code || 'INTEGRATION_REQUEST_FAILED';
  formatted.status = error.response?.status || null;
  formatted.requestId =
    error.response?.data?.error?.details?.requestId ||
    error.response?.headers?.['x-request-id'] ||
    null;
  return formatted;
};

const integrationService = {
  getDanlonSupportedPayTypes: async () => {
    try { return (await apiClient.get('/admin/danlon/payroll-settings/supported-pay-types')).data.data; }
    catch (error) { throw handleError(error); }
  },
  saveDanlonMappings: async (employerCvr, payload) => {
    try { return (await apiClient.put(`/admin/danlon/payroll-settings/${encodeURIComponent(employerCvr)}/mappings`, payload)).data.data; }
    catch (error) { throw handleError(error); }
  },
  listDanlonPayrollEmployers: async () => {
    try { return (await apiClient.get('/admin/danlon/payroll-settings')).data.data; }
    catch (error) { throw handleError(error); }
  },
  getDanlonPayrollSettings: async (employerCvr) => {
    try { return (await apiClient.get(`/admin/danlon/payroll-settings/${encodeURIComponent(employerCvr)}`)).data.data; }
    catch (error) { throw handleError(error); }
  },
  saveDanlonPayrollSettings: async (employerCvr, payload) => {
    try { return (await apiClient.put(`/admin/danlon/payroll-settings/${encodeURIComponent(employerCvr)}`, payload)).data.data; }
    catch (error) { throw handleError(error); }
  },
  getDanlonPayrollHistory: async (employerCvr) => {
    try { return (await apiClient.get(`/admin/danlon/payroll-settings/${encodeURIComponent(employerCvr)}/history`)).data.data; }
    catch (error) { throw handleError(error); }
  },
  previewDanlonPayrollImport: async (employerCvr, snapshot) => {
    try { return (await apiClient.post(`/admin/danlon/payroll-settings/${encodeURIComponent(employerCvr)}/import-preview`, { snapshot })).data.data; }
    catch (error) { throw handleError(error); }
  },
  // List all integrations (secrets masked as secretsSet booleans).
  getIntegrations: async () => {
    try {
      const response = await apiClient.get('/admin/integrations');
      return response.data.data;
    } catch (error) {
      throw handleError(error);
    }
  },

  // Save one integration's enabled flag + config (empty secret fields keep the
  // stored value).
  updateIntegration: async (provider, payload) => {
    try {
      const response = await apiClient.put(`/admin/integrations/${provider}`, payload);
      return response.data.data;
    } catch (error) {
      throw handleError(error);
    }
  },

  // Test a connection with the stored config plus any unsaved overrides.
  testIntegration: async (provider, config) => {
    try {
      const response = await apiClient.post(`/admin/integrations/${provider}/test`, config ? { config } : {});
      return response.data.data;
    } catch (error) {
      throw handleError(error);
    }
  },

  // Start the Danløn connect (OAuth) flow: returns the authorize URL the
  // browser should navigate to. The stored config must have the OAuth settings.
  getDanlonConnectUrl: async () => {
    try {
      const response = await apiClient.post('/admin/integrations/danlon/connect');
      return response.data.data.authorizeUrl;
    } catch (error) {
      throw handleError(error);
    }
  },

  /* ------------- API clients (scoped keys for the integration API) ------------- */

  // List API clients: [{ id, name, scopes, createdAt, revokedAt }]
  listApiClients: async () => {
    try {
      const response = await apiClient.get('/admin/api-clients');
      return response.data.data;
    } catch (error) {
      throw handleError(error);
    }
  },

  // Create an API client. Returns { id, name, scopes, key } — the plaintext
  // `key` is available ONLY in this response (stored hashed server-side).
  createApiClient: async ({ name, scopes, financialAccess }) => {
    try {
      const response = await apiClient.post('/admin/api-clients', { name, scopes, financialAccess });
      return response.data.data;
    } catch (error) {
      throw handleError(error);
    }
  },

  updateFinancialAccess: async (id, payload) => {
    try { return (await apiClient.put(`/admin/api-clients/${id}/financial-access`, payload)).data.data; }
    catch (error) { throw handleError(error); }
  },

  // Revoke an API client (idempotent). Returns { id, revokedAt }.
  revokeApiClient: async (id) => {
    try {
      const response = await apiClient.post(`/admin/api-clients/${id}/revoke`);
      return response.data.data;
    } catch (error) {
      throw handleError(error);
    }
  },

  /* --------------------- Webhook subscriptions & deliveries -------------------- */

  // List subscriptions: [{ id, name, url, eventTypes, active, createdAt }]
  listWebhookSubscriptions: async () => {
    try {
      const response = await apiClient.get('/admin/webhook-subscriptions');
      return response.data.data;
    } catch (error) {
      throw handleError(error);
    }
  },

  // Create a subscription. Returns { id, name, url, eventTypes, secret } — the
  // signing `secret` is available ONLY in this response.
  createWebhookSubscription: async ({ name, url, eventTypes, integrationProvider }) => {
    try {
      const payload = {
        name,
        url,
        eventTypes,
        ...(integrationProvider ? { integrationProvider } : {}),
      };
      const response = await apiClient.post('/admin/webhook-subscriptions', payload);
      return response.data.data;
    } catch (error) {
      throw handleError(error);
    }
  },

  // Delete a subscription. Returns { id }.
  deleteWebhookSubscription: async (id) => {
    try {
      const response = await apiClient.delete(`/admin/webhook-subscriptions/${id}`);
      return response.data.data;
    } catch (error) {
      throw handleError(error);
    }
  },

  // Recent outbox rows. V2 rows expose only their numeric changeId and
  // subscriptionId replay handles; the stored event payload remains private.
  listWebhookDeliveries: async (limit = 50) => {
    try {
      const response = await apiClient.get('/admin/webhook-deliveries', { params: { limit } });
      return response.data.data;
    } catch (error) {
      throw handleError(error);
    }
  },

  /* ----------------------- VikarCRM v2 runtime control ---------------------- */

  getVikarCrmV2Status: async () => {
    try {
      const response = await apiClient.get('/admin/integrations/vikarcrm-v2/status');
      return response.data.data;
    } catch (error) {
      throw handleError(error);
    }
  },

  testVikarCrmV2Readiness: async () => {
    try {
      const response = await apiClient.post('/admin/integrations/vikarcrm-v2/readiness-test');
      return response.data.data;
    } catch (error) {
      throw handleError(error);
    }
  },

  setVikarCrmV2RequestedState: async ({ enabled, confirmation, stateRevision }) => {
    try {
      const payload = enabled
        ? { enabled: true, confirmation, stateRevision }
        : { enabled: false };
      const response = await apiClient.put('/admin/integrations/vikarcrm-v2/requested-state', payload);
      return response.data.data;
    } catch (error) {
      throw handleError(error);
    }
  },

  runVikarCrmV2SmokeTest: async () => {
    try {
      const response = await apiClient.post('/admin/integrations/vikarcrm-v2/smoke-test');
      return response.data.data;
    } catch (error) {
      throw handleError(error);
    }
  },
};

export default integrationService;
